import { PRODUCT_NAME, PRODUCT_SUBTITLE } from '@goanna-im/shared';

// Faint, mirrored keywords that drift up behind the page like teleprompter text —
// unreadable, but seeding the SaaS story in the corner of your eye.
const KEYWORDS = [
  'Voice-follow scrolling',
  'Never lose your place',
  'AI producer',
  'Hot facts on cue',
  'Ask the next question',
  'Runs on any tablet',
  'Under the glass',
  'Hands-free control',
  'Cloud production',
  'Your script · your cues · your show',
  'Interview Internet Machine',
  'Cues · timers · run sheets',
  'Look them in the eye',
  'Studio-grade prompting',
  'Set it once — it follows you',
  'No sticky notes',
  'No second monitor',
  'One screen · total control',
  'Bring your own tablet',
  'Produce like a pro',
  'Reflective teleprompter',
  'Live remote production',
  'Subscribe and go live',
];

export function FrontPage({ navigate }: { navigate: (to: string) => void }) {
  return (
    <div className="sales">
      <div className="tp-bg" aria-hidden="true">
        <div className="tp-bg-mirror">
          <div className="tp-bg-scroll">
            {[...KEYWORDS, ...KEYWORDS].map((w, i) => (
              <span key={i}>{w}</span>
            ))}
          </div>
        </div>
      </div>
      <div className="tp-veil" aria-hidden="true" />

      <div className="sales-wrap">
        <section className="hero">
          <span className="kicker">{PRODUCT_SUBTITLE}</span>
          <h1>The teleprompter that follows your voice.</h1>
          <p className="lead">
            {PRODUCT_NAME} turns any tablet under teleprompter glass into a reflective display,
            interview assistant and producer console in one — voice-follow scrolling, live cues, hot
            facts and AI, completely hands-free.
          </p>
          <div className="cta-row">
            <button className="primary btn-lg" onClick={() => navigate('/login')}>
              Producer sign in →
            </button>
            <button className="btn-lg" onClick={() => navigate('/presenter')}>
              Open presenter
            </button>
          </div>
          <span className="muted" style={{ fontSize: '0.85rem' }}>
            Invite-only during early access · Pricing to be announced
          </span>
        </section>

        <section className="section">
          <h2>Not just words on a screen</h2>
          <div className="feature-grid">
            <div className="feature">
              <div className="ico">🎤</div>
              <h3>Voice-follow</h3>
              <p className="muted">
                It scrolls as you speak and holds the line you're reading — look up, stay natural, and
                it waits when you go off-book.
              </p>
            </div>
            <div className="feature">
              <div className="ico">🎬</div>
              <h3>Producer console</h3>
              <p className="muted">
                Next question, hot fact, camera and video cues, timers and messages — sent live to the
                glass from any PC or phone.
              </p>
            </div>
            <div className="feature">
              <div className="ico">✨</div>
              <h3>AI producer</h3>
              <p className="muted">
                Live follow-up questions, facts and context as the interview unfolds — you approve
                before anything reaches the screen.
              </p>
            </div>
            <div className="feature">
              <div className="ico">📱</div>
              <h3>Any tablet, any glass</h3>
              <p className="muted">
                Reuse an old tablet under a beamsplitter. Install it as an app, works offline, and
                drives from a Bluetooth remote.
              </p>
            </div>
          </div>
        </section>

        <section className="section">
          <h2>Built for people who talk to camera</h2>
          <div className="audience">
            {['Interviewers', 'Podcasters', 'Streamers', 'Journalists', 'Educators', 'Presenters', 'Producers'].map(
              (a) => (
                <span className="pill" key={a}>
                  {a}
                </span>
              ),
            )}
          </div>
        </section>

        <section className="section">
          <h2>How it works</h2>
          <div className="feature-grid">
            <div className="feature">
              <h3>1 · Sign in &amp; script</h3>
              <p className="muted">Producers sign in, then write or paste the script and run sheet.</p>
            </div>
            <div className="feature">
              <h3>2 · Pair the tablet</h3>
              <p className="muted">Start a session and scan the code onto the tablet under the glass.</p>
            </div>
            <div className="feature">
              <h3>3 · Go live</h3>
              <p className="muted">Speak — it follows you. Fire cues, facts and questions as you go.</p>
            </div>
          </div>
        </section>

        <section className="section pricing-tba">
          <h2>Pricing</h2>
          <p className="big">Coming soon.</p>
          <p className="muted">
            A genuinely useful free local edition, a Pro subscription for cloud, AI and remote
            production, and pay-as-you-go event passes. Details to be announced.
          </p>
        </section>

        <section className="section" style={{ textAlign: 'center' }}>
          <h2>Ready to produce like a pro?</h2>
          <div className="cta-row" style={{ justifyContent: 'center' }}>
            <button className="primary btn-lg" onClick={() => navigate('/login')}>
              Producer sign in
            </button>
            <button className="btn-lg" onClick={() => navigate('/presenter')}>
              Open presenter
            </button>
          </div>
        </section>
      </div>

      <footer className="sales-footer">
        {PRODUCT_NAME} — {PRODUCT_SUBTITLE}. IM means Internet Machine.
      </footer>
    </div>
  );
}
